import React, { useEffect, useState, useCallback } from "react";
import { api } from "../api/client";
import { RefreshCw, Filter, ChevronLeft, ChevronRight } from "lucide-react";

const ACTION_COLORS = {
  login_success: "bg-emerald-100 text-emerald-700",
  login_failure: "bg-red-100 text-red-700",
  logout: "bg-slate-100 text-slate-600",
  dashboard_view: "bg-blue-100 text-blue-700",
  widget_run: "bg-purple-100 text-purple-700",
  report_create: "bg-cyan-100 text-cyan-700",
  report_update: "bg-cyan-100 text-cyan-700",
  report_delete: "bg-red-100 text-red-700",
  report_run: "bg-indigo-100 text-indigo-700",
  report_export: "bg-amber-100 text-amber-700",
  admin_user_create: "bg-emerald-100 text-emerald-700",
  admin_user_update: "bg-amber-100 text-amber-700",
  admin_user_delete: "bg-red-100 text-red-700",
  admin_users_view: "bg-slate-100 text-slate-600",
  admin_audit_view: "bg-slate-100 text-slate-600",
};

const ALL_ACTIONS = [
  "login_success", "login_failure", "logout",
  "dashboard_view", "widget_run",
  "report_create", "report_update", "report_delete", "report_run", "report_export",
  "admin_user_create", "admin_user_update", "admin_user_delete",
];

export default function AdminAudit() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("");
  const [filterUsername, setFilterUsername] = useState("");
  const [showFilter, setShowFilter] = useState(false);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (filterAction) params.action = filterAction;
      if (filterUsername) params.username = filterUsername;
      const res = await api.get("/admin/audit", { params });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Audit fetch error", err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filterAction, filterUsername]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="flex-1 overflow-auto">
      <div className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-8 backdrop-blur-md">
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Audit Log
          <span className="ml-2 text-sm font-normal text-slate-400">{total.toLocaleString()} events</span>
        </h1>
        <div className="flex items-center gap-2">
          <button
            data-testid="filter-btn"
            onClick={() => setShowFilter((v) => !v)}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${showFilter ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            <Filter size={13} /> Filter
          </button>
          <button
            data-testid="refresh-audit-btn"
            onClick={fetchAudit}
            className="flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      <div className="p-8 space-y-4">
        {/* Filters */}
        {showFilter && (
          <div data-testid="audit-filters" className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Action</label>
              <select
                data-testid="filter-action-select"
                value={filterAction}
                onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white min-w-48"
              >
                <option value="">All Actions</option>
                {ALL_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Username</label>
              <input
                data-testid="filter-username-input"
                type="text"
                value={filterUsername}
                onChange={(e) => { setFilterUsername(e.target.value); setPage(1); }}
                placeholder="Search username…"
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <button
              onClick={() => { setFilterAction(""); setFilterUsername(""); setPage(1); }}
              className="h-9 px-3 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Clear
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading audit events…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    {["Time", "User", "Action", "Resource", "IP", "Details"].map((h) => (
                      <th key={h} className="p-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-slate-400">No events found</td></tr>
                  )}
                  {events.map((e) => (
                    <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      <td className="p-4 text-xs font-mono text-slate-500 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="p-4 font-medium text-slate-900">{e.username || <span className="text-slate-300">—</span>}</td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[e.action] || "bg-slate-100 text-slate-600"}`}>
                          {e.action}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500 text-xs">
                        {e.resource_type ? `${e.resource_type}${e.resource_id ? ` #${e.resource_id}` : ""}` : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="p-4 font-mono text-xs text-slate-400">{e.ip_address || "—"}</td>
                      <td className="p-4 text-xs text-slate-400 max-w-xs truncate">
                        {e.details ? JSON.stringify(e.details) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
              <p className="text-xs text-slate-400">
                Page {page} of {totalPages} &bull; {total} total events
              </p>
              <div className="flex items-center gap-2">
                <button
                  data-testid="prev-page-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft size={15} />
                </button>
                <button
                  data-testid="next-page-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
